import { AnimatePresence, motion } from "framer-motion";
import React, { useEffect, useState } from "react";

const containerVariants = {
  initial: { opacity: 0, x: "100%" },
  animate: {
    opacity: 1,
    x: 0,
    transition: {
      when: "beforeChildren",
      staggerChildren: 0.2,
      duration: 0.5,
      ease: "easeInOut",
    },
  },
  exit: { opacity: 0, x: "-100%" },
};

const itemVariants = {
  initial: { opacity: 0, y: 20 },
  animate: {
    opacity: 1,
    y: 0,
    transition: { duration: 0.4, ease: "easeOut" },
  },
};

const results = [
  {
    id: 1,
    topic: "reporting",
    timestamp: "2024-05-17T10:00:00Z",
    content: "Daily report generated successfully",
  },
  {
    id: 2,
    topic: "auth-service",
    timestamp: "2024-05-15T22:00:00Z",
    content: "Previous day login event",
  },
  {
    id: 3,
    topic: "auth-service",
    timestamp: "2024-05-16T14:05:00Z",
    content: "User login succeeded for admin",
  },
  {
    id: 4,
    topic: "email-service",
    timestamp: "2024-05-19T14:30:00Z",
    content: "Email delivery delayed for batch job",
  },
  {
    id: 5,
    topic: "log-parser",
    timestamp: "2024-05-16T14:45:00Z",
    content: "Parsing complete for daily logs",
  },
  {
    id: 6,
    topic: "log-parser",
    timestamp: "2024-05-16T15:00:00Z",
    content: "Error logs failed to parse due to malformed JSON",
  },
  {
    id: 7,
    topic: "log-parser",
    timestamp: "2024-05-16T15:20:00Z",
    content: "Error logs normalized and archived",
  },
  {
    id: 8,
    topic: "scheduler",
    timestamp: "2024-05-16T15:30:00Z",
    content: "Scheduled job execution started",
  },
  {
    id: 9,
    topic: "data-sync",
    timestamp: "2024-05-16T15:40:00Z",
    content: "Final sync event within time window",
  },
  {
    id: 10,
    topic: "data-sync",
    timestamp: "2024-05-16T15:41:00Z",
    content: "Post-window sync should not appear",
  },
  {
    id: 11,
    topic: "email-service",
    timestamp: "2024-05-16T16:10:00Z",
    content: "Email queue cleared after time window",
  },
  {
    id: 12,
    topic: "auth-service",
    timestamp: "2024-05-16T13:00:00Z",
    content: "User login attempt outside filter window",
  },
  {
    id: 13,
    topic: "auth-service",
    timestamp: "2024-05-16T13:45:00Z",
    content: "User login failed due to invalid credentials",
  },
];

const Slide2Analysis = () => {
  const [searchText, setSearchText] = useState("");
  const [dateRange, setDateRange] = useState({ start: null, end: null });
  const [filterPhase, setFilterPhase] = useState("none"); // "date" → "text"
  const [fromDateText, setFromDateText] = useState("");
  const [toDateText, setToDateText] = useState("");
  const fullText = "error logs";

  useEffect(() => {
    const timers = [];
    let i = 0;

    const resetAnimation = () => {
      // Explicitly reset state before restarting
      setSearchText("");
      setFromDateText("");
      setToDateText("");
      setDateRange({ start: null, end: null });
      setFilterPhase("none");

      timers.push(
        setTimeout(() => {
          const start = "2024-05-16 14:00";
          const end = "2024-05-17 17:40";
          setFromDateText("");
          setToDateText("");
          timers.push(
            setTimeout(() => {
              setFromDateText(start);
              setDateRange((prev) => ({
                ...prev,
                start: new Date("2024-05-16T14:00:00Z").getTime(),
              }));
              timers.push(
                setTimeout(() => {
                  setToDateText(end);
                  setDateRange((prev) => ({
                    ...prev,
                    end: new Date("2024-05-17T17:40:00").getTime(),
                  }));
                  setFilterPhase("date");

                  timers.push(
                    setTimeout(() => {
                      setFilterPhase("text");
                      startTyping();
                    }, 800)
                  );
                }, 800)
              );
            }, 700)
          );
        }, 700)
      );
    };

    const startTyping = () => {
      i = 0;
      const type = () => {
        if (i <= fullText.length) {
          setSearchText(fullText.slice(0, i));
          i++;
          timers.push(setTimeout(type, i < 4 ? 300 : 150));
        } else {
          timers.push(setTimeout(() => resetAnimation(), 1000));
        }
      };
      type();
    };

    resetAnimation();
    return () => {
      timers.forEach(clearTimeout);
    };
  }, []);

  return (
    <motion.section
      variants={containerVariants}
      initial="initial"
      animate="animate"
      className="h-content min-h-screen flex flex-col md:flex-row-reverse items-center justify-center bg-gray-50 p-4 space-y-8 md:space-y-0 md:space-x-12 md:gap-10"
    >
      {/* Textual explanation on the right (or top on mobile) */}
      <motion.div
        variants={itemVariants}
        className="w-4/5 md:w-2/5 text-center md:text-left"
      >
        <h2 className="text-2xl font-bold mb-4">Data Insights at a Glance</h2>
        <p className="text-gray-700 text-justify">
          Effortlessly filter and search through your data by topic, name or
          time range. Quickly identify events, see the outliers, and take
          informed decisions in real time using your dashboard's analytical
          tools.
        </p>
      </motion.div>

      {/* Animated filtering/searching on the left (or bottom on mobile) */}
      <motion.div className="w-4/5 md:w-2/5 h-[500px] space-y-4 bg-gray-100 rounded-2xl p-4 overflow-hidden">
        <motion.div
          variants={itemVariants}
          className="w-full min-h-[48px] bg-white shadow-md rounded px-4 flex items-center font-mono"
        >
          🔍 {searchText}
        </motion.div>
        <motion.div
          variants={itemVariants}
          className="w-full h-12 bg-white shadow-md rounded px-4 flex items-center font-mono"
        >
          📅 Date:{" "}
          {fromDateText ? (
            <span className="ml-2 font-semibold">{fromDateText}</span>
          ) : (
            <span className="ml-2 font-semibold opacity-50">
              &nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;
            </span>
          )}
          &nbsp;&nbsp;to
          {toDateText ? (
            <span className="ml-2 font-semibold">{toDateText}</span>
          ) : (
            <span className="ml-2 font-semibold opacity-50">
              &nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;
            </span>
          )}
        </motion.div>
        <div className="max-h-[21rem] overflow-hidden space-y-3">
          {results.slice(0, 7).map((log) => {
            const logTime = new Date(log.timestamp).getTime();
            const matchesDate =
              (!dateRange.start || logTime >= dateRange.start) &&
              (!dateRange.end || logTime <= dateRange.end);
            const matchesText = log.content
              .toLowerCase()
              .includes(searchText.toLowerCase());
            const matches =
              matchesDate && (filterPhase !== "text" || matchesText);
            const regex = new RegExp(`(${searchText})`, "gi");
            const highlightedContent = log.content.replace(
              regex,
              "<mark class='bg-yellow-200'>$1</mark>"
            );
            return (
              <motion.div
                key={log.id}
                className={`bg-white p-3 rounded shadow transition-all duration-300 ${
                  matches ? "opacity-100" : "opacity-0 hidden"
                }`}
              >
                <div className="text-sm font-semibold text-delft-blue">
                  {log.topic}
                </div>
                <div className="text-xs text-gray-500">
                  {new Date(log.timestamp).toLocaleString()}
                </div>
                <div
                  className="text-sm text-gray-800 mt-1"
                  dangerouslySetInnerHTML={{ __html: highlightedContent }}
                />
              </motion.div>
            );
          })}
        </div>
      </motion.div>
    </motion.section>
  );
};

export default Slide2Analysis;
